// packages/web-ui/src/components/kb-browser/group-by-level.ts
//
// Groups KB entities for the `<KbEntityTree>` by hierarchy level (system-of-
// systems → systems → sub-systems → components), then sub-categorises each
// numbered level into three sub-groups based on entity kind:
//
//   - Building blocks (`part def` → kind === 'part')
//   - Specs            (`part X : Y` value-bound usage → kind === 'part-usage')
//   - Flows            (connection / interface / port defs that live at the
//                       level; usually empty after the W6 KB restructure but
//                       defensively bucketed)
//
// Non-hierarchy buckets (`'flows'`, `'metadata'`, `'unknown'`) emit as their
// own top-level groups so the user sees them separately from numbered levels.
//
// The function returns the existing flat `HierarchyGroup[]` shape so
// `<KbEntityTree>` can swap helpers without touching its render path — the
// sub-category is encoded into the group `key` as
// `"Level 4 — Components / Specs"`. Empty groups are omitted.

import type { KbEntity, KbEntityKind, KbHierarchyLevel } from '@tessa-lean/base/api-schemas/index.js';
import { KIND_ORDER, type HierarchyGroup } from './group-by-hierarchy.js';

const LEVEL_ORDER: ReadonlyArray<KbHierarchyLevel> = [
  'system-of-systems',
  'systems',
  'sub-systems',
  'components',
  'flows',
  'metadata',
  'unknown',
];

const LEVEL_LABEL: Record<KbHierarchyLevel, string> = {
  'system-of-systems': 'Level 1 — System of Systems',
  systems: 'Level 2 — Systems',
  'sub-systems': 'Level 3 — Sub-systems',
  components: 'Level 4 — Components',
  flows: 'Flows',
  metadata: 'Domain metadata',
  unknown: 'Other',
};

/**
 * Sub-categories shown inside each numbered hierarchy level. Their order
 * matches the way users typically read a level: structure (building blocks)
 * comes before value-bindings (specs), which come before cross-cutting flow
 * schemas that happen to be co-located.
 */
const SUBCATEGORY_ORDER = ['building-blocks', 'specs', 'flows', 'ports', 'values'] as const;
type SubCategory = typeof SUBCATEGORY_ORDER[number];

const SUBCATEGORY_LABEL: Record<SubCategory, string> = {
  'building-blocks': 'Building blocks',
  specs: 'Specs',
  flows: 'Flows',
  ports: 'Ports',
  values: 'Value attributes',
};

const SUBCATEGORY_RANK: Record<SubCategory, number> = Object.fromEntries(
  SUBCATEGORY_ORDER.map((k, i) => [k, i]),
) as Record<SubCategory, number>;

/** Kind → sub-category mapping inside a numbered hierarchy level. */
function subcategoryForKind(kind: KbEntityKind): SubCategory {
  switch (kind) {
    case 'part':
      return 'building-blocks';
    case 'part-usage':
      return 'specs';
    case 'connection':
    case 'connection-def':
    case 'interface':
    case 'interface-def':
      return 'flows';
    case 'port':
      return 'ports';
    case 'value-attribute':
      return 'values';
  }
}

const KIND_RANK: Record<string, number> = Object.fromEntries(
  KIND_ORDER.map((k, i) => [k, i]),
);

/** Levels that get sub-grouped by entity kind. Flows / metadata / unknown stay flat. */
const SUBGROUPED_LEVELS = new Set<KbHierarchyLevel>([
  'system-of-systems',
  'systems',
  'sub-systems',
  'components',
]);

/**
 * Returns the human-readable label for a hierarchy level. Used by the tree's
 * group header so callers stay decoupled from the enum encoding.
 */
export function levelLabel(level: KbHierarchyLevel): string {
  return LEVEL_LABEL[level];
}

function sortEntities(entities: KbEntity[]): void {
  entities.sort((a, b) => {
    const rankDiff = (KIND_RANK[a.kind] ?? 0) - (KIND_RANK[b.kind] ?? 0);
    if (rankDiff !== 0) return rankDiff;
    return a.qualifiedName.localeCompare(b.qualifiedName);
  });
}

/**
 * Groups entities by hierarchy level and sub-categorises numbered levels
 * (system-of-systems → components) into Building blocks / Specs / Flows /
 * Ports / Value attributes based on entity kind. Empty buckets are omitted.
 */
export function groupByLevel(entities: KbEntity[]): HierarchyGroup[] {
  // Level → SubCategory → entities. We use nested Maps so insertion order is
  // preserved per-level; we then walk in canonical order for emission.
  const buckets = new Map<KbHierarchyLevel, Map<SubCategory | '__flat__', KbEntity[]>>();

  for (const entity of entities) {
    const level: KbHierarchyLevel = entity.level ?? 'unknown';
    let levelBucket = buckets.get(level);
    if (levelBucket === undefined) {
      levelBucket = new Map();
      buckets.set(level, levelBucket);
    }
    const subKey: SubCategory | '__flat__' = SUBGROUPED_LEVELS.has(level)
      ? subcategoryForKind(entity.kind)
      : '__flat__';
    let subBucket = levelBucket.get(subKey);
    if (subBucket === undefined) {
      subBucket = [];
      levelBucket.set(subKey, subBucket);
    }
    subBucket.push(entity);
  }

  const out: HierarchyGroup[] = [];
  for (const level of LEVEL_ORDER) {
    const levelBucket = buckets.get(level);
    if (levelBucket === undefined || levelBucket.size === 0) continue;

    if (!SUBGROUPED_LEVELS.has(level)) {
      // Non-hierarchy bucket — emit a single flat group per level.
      const flat = levelBucket.get('__flat__');
      if (flat === undefined || flat.length === 0) continue;
      sortEntities(flat);
      out.push({ key: levelLabel(level), entities: flat });
      continue;
    }

    // Numbered hierarchy level — emit one sub-group per SubCategory.
    const orderedSubs: SubCategory[] = [...levelBucket.keys()]
      .filter((k): k is SubCategory => k !== '__flat__')
      .sort((a, b) => SUBCATEGORY_RANK[a] - SUBCATEGORY_RANK[b]);
    for (const sub of orderedSubs) {
      const subEntities = levelBucket.get(sub);
      if (subEntities === undefined || subEntities.length === 0) continue;
      sortEntities(subEntities);
      out.push({
        key: `${levelLabel(level)} / ${SUBCATEGORY_LABEL[sub]}`,
        entities: subEntities,
      });
    }
  }
  return out;
}
